
import React from "react";
import { UseFormReturn } from "react-hook-form";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { PhotoPositioningPreview } from "./PhotoPositioningPreview";

interface PhotoFormFieldsProps {
  form: UseFormReturn<any>;
  isAdmin?: boolean;
  onCopyFromCaption: () => void;
  disabled?: boolean;
  onYOffsetChange?: (value: number) => void;
  previewImageSrc?: string | null;
  showPositioningSection?: boolean;
  permissionNote?: string;
}

export const PhotoFormFields: React.FC<PhotoFormFieldsProps> = ({
  form,
  isAdmin,
  onCopyFromCaption,
  disabled,
  onYOffsetChange,
  previewImageSrc,
  showPositioningSection = true,
  permissionNote,
}) => {
  const photoCredit = (value?: string) => {
    const s = (value || "").trim();
    if (s === "Canva" || s === "from Canva") return "Photo from Canva.";
    if (s.toLowerCase().startsWith("from ")) return `Photo ${s}.`;
    if (!s) return "";
    return `Photo courtesy of ${s}.`;
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        {previewImageSrc && showPositioningSection && (
          <FormField
            control={form.control}
            name="yOffset"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Photo positioning</FormLabel>
                
                {/* Photo positioning preview */}
                <PhotoPositioningPreview 
                  imageSrc={previewImageSrc}
                  yOffset={field.value}
                  onYOffsetChange={(val) => {
                    field.onChange(val);
                    onYOffsetChange?.(val);
                  }}
                  disabled={disabled}
                />
                
                {/* Slider that moves with the preview */}
                <div className="mt-2 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">Shift down</span>
                    <span className="text-sm text-gray-500">Shift up</span>
                  </div>
                  <FormControl>
                    <Slider
                      min={-1}
                      max={1}
                      step={0.1}
                      value={[field.value]}
                      onValueChange={(values) => {
                        field.onChange(values[0]);
                        onYOffsetChange?.(values[0]);
                      }}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormDescription>
                    Adjust vertical positioning of the image
                  </FormDescription>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="photoReference"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Photo credit</FormLabel>
              <FormControl>
                <Input {...field} disabled={disabled} />
              </FormControl>
              <FormDescription>
                {`Photo credit will appear as: "${photoCredit(form.watch('photoReference'))}"`}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {permissionNote && permissionNote.trim() && (
          <FormItem>
            <FormLabel>Photo permission</FormLabel>
            <FormControl>
              <div 
                className="flex h-10 w-full rounded-md border border-input bg-gray-100 px-3 py-2 text-sm ring-offset-background cursor-not-allowed"
                style={{ color: '#484848' }}
              >
                {permissionNote}
              </div>
            </FormControl>
          </FormItem>
        )}

        <FormField
          control={form.control}
          name="caption"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Caption</FormLabel>
              <FormControl>
                <Textarea 
                  {...field} 
                  maxLength={1000}
                  disabled={disabled}
                  placeholder="Brief description of the photo"
                />
              </FormControl>
              <FormDescription>
                You may add a caption (max 1,000 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="altText"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between items-center">
                <FormLabel>Alt text for photo accessibility</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onCopyFromCaption}
                  disabled={!form.getValues("caption") || disabled}
                >
                  Copy from caption
                </Button>
              </div>
              <FormControl>
                <Textarea 
                  {...field} 
                  maxLength={1000}
                  disabled={disabled}
                  placeholder="Describe what is in the photo for screen readers"
                />
              </FormControl>
              <FormDescription>
                Briefly describe what is in the photo for screen readers (max 1,000 characters)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </Form>
  );
};
